import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // G.1: Subject Progress
  subjectProgress: [],
  subjectProgressLoading: false,
  subjectProgressError: null,

  // G.2: Subject Detail
  currentSubjectDetail: null,
  subjectDetailLoading: false,
  subjectDetailError: null,

  // G.3: Topic Mastery
  topicMastery: {
    topics: [],
    grouped: {
      mastered: [],
      learning: [],
      needsWork: []
    },
    summary: {
      mastered: 0,
      learning: 0,
      needsWork: 0,
      total: 0
    }
  },
  topicMasteryLoading: false,
  topicMasteryError: null,

  // G.4: Progress Timeline
  progressTimeline: [],
  timelineRange: '6months',
  timelineLoading: false,
  timelineError: null,
  overallTrend: 'stable',

  // G.5: Recommendations
  recommendations: [],
  recommendationsLoading: false,
  recommendationsError: null,

  // G.6: Resources
  resources: [],
  resourcesPagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },
  resourcesLoading: false,
  resourcesError: null,
  resourceFilters: {
    subject: null,
    type: null,
    difficulty: null,
    search: ''
  },
  recommendedResources: [],
  recommendedResourcesLoading: false,

  // G.7: Self Comparison
  comparison: null,
  comparisonLoading: false,
  comparisonError: null,

  // G.9: Chapter Progress
  chapterProgress: [],
  chapterProgressLoading: false,
  chapterProgressError: null,

  // G.8: Report
  reportGenerating: false,
  reportUrl: null,
  reportError: null,

  // Cache timestamps
  lastFetched: {
    subjectProgress: null,
    topicMastery: null,
    timeline: null,
    recommendations: null,
    comparison: null
  }
};

const studentProgressSlice = createSlice({
  name: 'studentProgress',
  initialState,
  reducers: {
    // === G.1: Subject Progress ===
    setSubjectProgressLoading: (state, action) => {
      state.subjectProgressLoading = action.payload;
    },
    setSubjectProgress: (state, action) => {
      state.subjectProgress = action.payload.subjects || [];
      state.subjectProgressLoading = false;
      state.subjectProgressError = null;
      state.lastFetched.subjectProgress = Date.now();
    },
    setSubjectProgressError: (state, action) => {
      state.subjectProgressError = action.payload;
      state.subjectProgressLoading = false;
    },

    // === G.2: Subject Detail ===
    setSubjectDetailLoading: (state, action) => {
      state.subjectDetailLoading = action.payload;
    },
    setSubjectDetail: (state, action) => {
      state.currentSubjectDetail = action.payload;
      state.subjectDetailLoading = false;
      state.subjectDetailError = null;
    },
    setSubjectDetailError: (state, action) => {
      state.subjectDetailError = action.payload;
      state.subjectDetailLoading = false;
    },
    clearSubjectDetail: (state) => {
      state.currentSubjectDetail = null;
      state.subjectDetailError = null;
    },

    // === G.3: Topic Mastery ===
    setTopicMasteryLoading: (state, action) => {
      state.topicMasteryLoading = action.payload;
    },
    setTopicMastery: (state, action) => {
      state.topicMastery = {
        topics: action.payload.topics || [],
        grouped: action.payload.grouped || { mastered: [], learning: [], needsWork: [] },
        summary: action.payload.summary || { mastered: 0, learning: 0, needsWork: 0, total: 0 }
      };
      state.topicMasteryLoading = false;
      state.topicMasteryError = null;
      state.lastFetched.topicMastery = Date.now();
    },
    setTopicMasteryError: (state, action) => {
      state.topicMasteryError = action.payload;
      state.topicMasteryLoading = false;
    },

    // === G.4: Progress Timeline ===
    setTimelineLoading: (state, action) => {
      state.timelineLoading = action.payload;
    },
    setProgressTimeline: (state, action) => {
      state.progressTimeline = action.payload.timeline || [];
      state.overallTrend = action.payload.overallTrend || 'stable';
      state.timelineRange = action.payload.timeRange || '6months';
      state.timelineLoading = false;
      state.timelineError = null;
      state.lastFetched.timeline = Date.now();
    },
    setTimelineError: (state, action) => {
      state.timelineError = action.payload;
      state.timelineLoading = false;
    },
    setTimelineRange: (state, action) => {
      state.timelineRange = action.payload;
    },

    // === G.5: Recommendations ===
    setRecommendationsLoading: (state, action) => {
      state.recommendationsLoading = action.payload;
    },
    setRecommendations: (state, action) => {
      state.recommendations = action.payload.recommendations || [];
      state.recommendationsLoading = false;
      state.recommendationsError = null;
      state.lastFetched.recommendations = Date.now();
    },
    setRecommendationsError: (state, action) => {
      state.recommendationsError = action.payload;
      state.recommendationsLoading = false;
    },

    // === G.6: Resources ===
    setResourcesLoading: (state, action) => {
      state.resourcesLoading = action.payload;
    },
    setResources: (state, action) => {
      state.resources = action.payload.resources || [];
      state.resourcesPagination = action.payload.pagination || state.resourcesPagination;
      state.resourcesLoading = false;
      state.resourcesError = null;
    },
    setResourcesError: (state, action) => {
      state.resourcesError = action.payload;
      state.resourcesLoading = false;
    },
    setResourceFilters: (state, action) => {
      state.resourceFilters = { ...state.resourceFilters, ...action.payload };
    },
    clearResourceFilters: (state) => {
      state.resourceFilters = {
        subject: null,
        type: null,
        difficulty: null,
        search: ''
      };
    },
    setRecommendedResourcesLoading: (state, action) => {
      state.recommendedResourcesLoading = action.payload;
    },
    setRecommendedResources: (state, action) => {
      state.recommendedResources = action.payload.recommendations || [];
      state.recommendedResourcesLoading = false;
    },

    // === G.7: Self Comparison ===
    setComparisonLoading: (state, action) => {
      state.comparisonLoading = action.payload;
    },
    setComparison: (state, action) => {
      state.comparison = action.payload;
      state.comparisonLoading = false;
      state.comparisonError = null;
      state.lastFetched.comparison = Date.now();
    },
    setComparisonError: (state, action) => {
      state.comparisonError = action.payload;
      state.comparisonLoading = false;
    },

    // === G.9: Chapter Progress ===
    setChapterProgressLoading: (state, action) => {
      state.chapterProgressLoading = action.payload;
    },
    setChapterProgress: (state, action) => {
      state.chapterProgress = action.payload.chapters || [];
      state.chapterProgressLoading = false;
      state.chapterProgressError = null;
    },
    setChapterProgressError: (state, action) => {
      state.chapterProgressError = action.payload;
      state.chapterProgressLoading = false;
    },
    clearChapterProgress: (state) => {
      state.chapterProgress = [];
      state.chapterProgressError = null;
    },

    // === G.8: Report ===
    setReportGenerating: (state, action) => {
      state.reportGenerating = action.payload;
    },
    setReportUrl: (state, action) => {
      state.reportUrl = action.payload;
      state.reportGenerating = false;
      state.reportError = null;
    },
    setReportError: (state, action) => {
      state.reportError = action.payload;
      state.reportGenerating = false;
    },
    clearReportUrl: (state) => {
      state.reportUrl = null;
      state.reportError = null;
    },

    // === General ===
    resetProgressState: () => initialState,
    clearAllErrors: (state) => {
      state.subjectProgressError = null;
      state.subjectDetailError = null;
      state.topicMasteryError = null;
      state.timelineError = null;
      state.recommendationsError = null;
      state.resourcesError = null;
      state.comparisonError = null;
      state.reportError = null;
    }
  }
});

// Export actions
export const {
  // Subject Progress
  setSubjectProgressLoading,
  setSubjectProgress,
  setSubjectProgressError,
  // Subject Detail
  setSubjectDetailLoading,
  setSubjectDetail,
  setSubjectDetailError,
  clearSubjectDetail,
  // Topic Mastery
  setTopicMasteryLoading,
  setTopicMastery,
  setTopicMasteryError,
  // Timeline
  setTimelineLoading,
  setProgressTimeline,
  setTimelineError,
  setTimelineRange,
  // Recommendations
  setRecommendationsLoading,
  setRecommendations,
  setRecommendationsError,
  // Resources
  setResourcesLoading,
  setResources,
  setResourcesError,
  setResourceFilters,
  clearResourceFilters,
  setRecommendedResourcesLoading,
  setRecommendedResources,
  // Comparison
  setComparisonLoading,
  setComparison,
  setComparisonError,
  // Chapter Progress
  setChapterProgressLoading,
  setChapterProgress,
  setChapterProgressError,
  clearChapterProgress,
  // Report
  setReportGenerating,
  setReportUrl,
  setReportError,
  clearReportUrl,
  // General
  resetProgressState,
  clearAllErrors
} = studentProgressSlice.actions;

// Selectors
export const selectSubjectProgress = (state) => state.studentProgress.subjectProgress;
export const selectSubjectProgressLoading = (state) => state.studentProgress.subjectProgressLoading;
export const selectSubjectProgressError = (state) => state.studentProgress.subjectProgressError;

export const selectSubjectDetail = (state) => state.studentProgress.currentSubjectDetail;
export const selectSubjectDetailLoading = (state) => state.studentProgress.subjectDetailLoading;
export const selectSubjectDetailError = (state) => state.studentProgress.subjectDetailError;

export const selectTopicMastery = (state) => state.studentProgress.topicMastery;
export const selectTopicMasteryLoading = (state) => state.studentProgress.topicMasteryLoading;
export const selectTopicMasteryError = (state) => state.studentProgress.topicMasteryError;

export const selectProgressTimeline = (state) => state.studentProgress.progressTimeline;
export const selectTimelineRange = (state) => state.studentProgress.timelineRange;
export const selectTimelineLoading = (state) => state.studentProgress.timelineLoading;
export const selectTimelineError = (state) => state.studentProgress.timelineError;
export const selectOverallTrend = (state) => state.studentProgress.overallTrend;

export const selectRecommendations = (state) => state.studentProgress.recommendations;
export const selectRecommendationsLoading = (state) => state.studentProgress.recommendationsLoading;
export const selectRecommendationsError = (state) => state.studentProgress.recommendationsError;

export const selectResources = (state) => state.studentProgress.resources;
export const selectResourcesPagination = (state) => state.studentProgress.resourcesPagination;
export const selectResourcesLoading = (state) => state.studentProgress.resourcesLoading;
export const selectResourcesError = (state) => state.studentProgress.resourcesError;
export const selectResourceFilters = (state) => state.studentProgress.resourceFilters;
export const selectRecommendedResources = (state) => state.studentProgress.recommendedResources;
export const selectRecommendedResourcesLoading = (state) => state.studentProgress.recommendedResourcesLoading;

export const selectComparison = (state) => state.studentProgress.comparison;
export const selectComparisonLoading = (state) => state.studentProgress.comparisonLoading;

export const selectReportGenerating = (state) => state.studentProgress.reportGenerating;
export const selectReportUrl = (state) => state.studentProgress.reportUrl;

export const selectLastFetched = (state) => state.studentProgress.lastFetched;

export const selectChapterProgress = (state) => state.studentProgress.chapterProgress;
export const selectChapterProgressLoading = (state) => state.studentProgress.chapterProgressLoading;
export const selectChapterProgressError = (state) => state.studentProgress.chapterProgressError;

// Cache validation helpers (5 minutes TTL)
const CACHE_TTL = 5 * 60 * 1000;

export const isSubjectProgressCacheValid = (state) => {
  const lastFetched = state.studentProgress.lastFetched.subjectProgress;
  return lastFetched && Date.now() - lastFetched < CACHE_TTL;
};

export const isTopicMasteryCacheValid = (state) => {
  const lastFetched = state.studentProgress.lastFetched.topicMastery;
  return lastFetched && Date.now() - lastFetched < CACHE_TTL;
};

export const isTimelineCacheValid = (state) => {
  const lastFetched = state.studentProgress.lastFetched.timeline;
  return lastFetched && Date.now() - lastFetched < CACHE_TTL;
};

export const isRecommendationsCacheValid = (state) => {
  const lastFetched = state.studentProgress.lastFetched.recommendations;
  return lastFetched && Date.now() - lastFetched < CACHE_TTL;
};

export default studentProgressSlice.reducer;
